import { Avatar, Chip, CircularProgress, makeStyles, Paper, Popper, PopperProps } from '@material-ui/core';
import TextField from '@material-ui/core/TextField';
import { Autocomplete } from '@material-ui/lab';
import clsx from 'clsx';
import { MouseEvent, ReactElement, ReactNode, Reducer, useCallback, useMemo, useReducer, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Option } from 'src/modules/i18n-helpers';

import { getIcon, IconType } from '../modules/data';
import { getLabel } from '../modules/labels';

import AutocompleteItem from './AutocompleteItem';

const useStyles = makeStyles((theme) => ({
  checkbox: { marginRight: theme.spacing(1), padding: 0 },
  chip: {
    marginTop: theme.spacing(0.25),
    marginBottom: theme.spacing(0.25),
    '& .MuiChip-label': {
      marginLeft: '8px',
    },
  },
  iconImage: { display: 'inline-block', width: 20, height: 'auto', marginRight: theme.spacing(1) },
  notchedOutline: {
    borderColor: theme.palette.primary.main,
    borderWidth: 2,
  },
  selectAll: {
    display: 'flex',
    marginLeft: theme.spacing(1),
    width: '95%',
    justifyContent: 'left',
    paddingTop: theme.spacing(1),
  },
  heightReduce: {
    paddingTop: 0,
    marginTop: 0,
    marginBottom: 0,
  },
  small: {
    minWidth: '8px !important',
  },
  noLabel: {
    '& .MuiChip-label': {
      display: 'none',
    },
    '& .MuiChip-avatarSmall': {
      marginRight: '6px',
    },
  },
  hidden: {
    opacity: 0.5,
  },
}));

interface GroupOption {
  value: string;
  label: string;
  group: string;
}

type FilterAutocompleteProps = {
  name: string;
  defaultValue?: string[];
  label: string;
  iconType?: IconType;
  IconComponent?: ({ value }: { value: string }) => ReactElement;
  disableClearable?: boolean;
  options: GroupOption[];
  isLoading?: boolean;
  highlightTextField?: boolean;
  onChipDelete?: (value: string) => void;
  selectAllOnDefault?: boolean;
  groupBy?: (option: string) => string;
  className?: string;
  shownTags?: number;
  noLabel?: boolean;
  smallInput?: boolean;
  groups: Option[];
  historicOptions: string[];
};

type Action = { type: 'add'; suggestion: string } | { type: 'reset' };

const reducer: Reducer<Set<string>, Action> = (suggestions, action) => {
  switch (action.type) {
    case 'reset':
      return new Set();
    case 'add':
      return new Set(suggestions).add(action.suggestion);
    default:
      throw new Error();
  }
};

const FilterAutocompleteGroupSelect = ({
  name,
  defaultValue,
  label,
  iconType,
  IconComponent,
  disableClearable = false,
  options,
  isLoading = false,
  highlightTextField,
  onChipDelete,
  groupBy,
  className,
  shownTags = 5,
  noLabel = false,
  smallInput = false,
  groups,
  historicOptions = [],
}: FilterAutocompleteProps) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const [suggestions, dispatch] = useReducer(reducer, new Set<string>());
  const { setValue, getValues } = useFormContext();

  // Create hierarchical options: groups first, then their children
  const hierarchicalOptions = useMemo(() => {
    const result: string[] = [];
    groups.forEach((groupName) => {
      result.push(groupName.value);
      const groupOptions = options.filter((option) => option.group === groupName.value).map((option) => option.value);
      result.push(...groupOptions);
    });
    return result;
  }, [options, groups]);

  // Memoize grouped options for performance
  const groupedOptions = useMemo(
    () =>
      options.reduce(
        (acc, option) => {
          if (!acc[option.group]) {
            acc[option.group] = [];
          }
          acc[option.group].push(option);
          return acc;
        },
        {} as Record<string, GroupOption[]>,
      ),
    [options],
  );

  const handleSelectAll = (event: MouseEvent) => {
    event.preventDefault();
    let newInput: typeof hierarchicalOptions;
    const suggArr = Array.from(suggestions);
    switch (getValues(name)?.length) {
      case 0:
        newInput = suggArr;
        break;
      default:
        newInput = [];
        break;
    }
    setValue(name, newInput);
  };

  const handleGroupClick = useCallback(
    (groupName: string, currentValues: string[]) => {
      const groupOptions = groupedOptions[groupName];
      if (!groupOptions) return;
      const groupValues = groupOptions.map((option) => option.value);
      const allGroupSelected = groupValues.every((value) => currentValues.includes(value));

      const newValues = allGroupSelected
        ? currentValues.filter((value) => !groupValues.includes(value))
        : [...new Set([...currentValues, ...groupValues])];
      setValue(name, newValues);
    },
    [groupedOptions, name, setValue],
  );

  const handleOnChange = useCallback(
    (newValues: string[]) => {
      const currentValues = getValues(name) || [];
      if (currentValues.length > newValues.length) {
        // remove items
        const removedItems = currentValues.filter((value: string) => !newValues.includes(value));
        const isGroup = groups.find((g) => g.value === removedItems);
        if (isGroup) handleGroupClick(removedItems[0], currentValues);
        setValue(name, newValues);
      } else {
        // add items
        const lastItem = newValues[newValues.length - 1];
        const isGroup = groups.find((g) => g.value === lastItem);
        if (isGroup) {
          handleGroupClick(lastItem, currentValues);
        } else {
          if (currentValues.includes(lastItem)) {
            setValue(
              name,
              currentValues.filter((v: string) => v === lastItem),
            );
          }
          setValue(name, [...currentValues, lastItem]);
        }
      }
    },
    [getValues, name, groups, handleGroupClick, setValue],
  );

  const getGroupState = (groupName: string, currentValues: string[]) => {
    const groupOptions = groupedOptions[groupName];
    if (!groupOptions) return { selected: false, indeterminate: false };

    const groupValues = groupOptions.map((option) => option.value);
    const selectedCount = groupValues.filter((value) => currentValues.includes(value)).length;

    return {
      selected: selectedCount === groupValues.length,
      indeterminate: selectedCount > 0 && selectedCount < groupValues.length,
    };
  };

  const renderOption = (value: string, { selected }: { selected: boolean }) => {
    const isGroup = groups.find((g) => g.value === value);

    if (isGroup) {
      // hide the group on autocomplete to avoid confusing selections
      if (inputValue) return null;
      const currentValues = getValues(name) || [];
      const groupState = getGroupState(value, currentValues);
      return (
        <AutocompleteItem
          checked={groupState.selected}
          indeterminate={groupState.indeterminate}
          value=""
          iconType={iconType}
          alt={label}
          IconComponent={IconComponent}
          label={getLabel(groups, value)}
          boldLabel
        />
      );
    }
    if (!suggestions.has(value)) dispatch({ type: 'add', suggestion: value });
    return (
      <div style={{ paddingLeft: '20px', display: 'flex' }}>
        <AutocompleteItem
          checked={selected}
          value={value}
          iconType={iconType}
          alt={label}
          IconComponent={IconComponent}
          label={getLabel(options, value)}
          historicOption={historicOptions.includes(value)}
        />
      </div>
    );
  };

  const AutocompletePopper = ({ children, ...rest }: PopperProps) => (
    <Popper {...rest}>
      <Paper>
        <div
          role="button"
          tabIndex={0}
          className={clsx('MuiPaper-root', 'MuiAutocomplete-paper', 'MuiPaper-elevation1')}
          onMouseDown={handleSelectAll}
        >
          <li className="MuiAutocomplete-option">
            <AutocompleteItem
              value="all"
              label={t('common.all')}
              checked={getValues(name)?.length === hierarchicalOptions?.length}
              indeterminate={getValues(name)?.length !== hierarchicalOptions?.length && getValues(name)?.length !== 0}
            />
          </li>
        </div>
        {children as ReactNode}
      </Paper>
    </Popper>
  );

  return (
    <Controller
      name={name}
      defaultValue={defaultValue}
      render={({ field: { onChange, ...field } }): ReactElement => (
        <Autocomplete
          {...field}
          inputValue={inputValue}
          onBlur={() => setInputValue('')}
          onKeyDown={(event) => {
            const suggArr = Array.from(suggestions);
            switch (event.key) {
              case 'a': // STRG + A should select all suggestions
                if (event.ctrlKey || event.metaKey) {
                  onChange(suggArr);
                }
                break;
              default:
                break;
            }
          }}
          PopperComponent={AutocompletePopper}
          onChange={(e, value) => {
            e.stopPropagation();
            handleOnChange(value);
          }}
          onInputChange={() => dispatch({ type: 'reset' })}
          disableClearable={disableClearable}
          multiple
          options={hierarchicalOptions}
          limitTags={shownTags}
          className={className}
          disableCloseOnSelect
          getOptionLabel={(option: string) => getLabel(options, option)}
          noOptionsText={t('carsTable.noEntriesFound')}
          groupBy={groupBy}
          renderOption={renderOption}
          renderInput={(params): ReactElement => (
            <TextField
              {...params}
              onChange={(event) => {
                setInputValue(event.currentTarget.value);
              }}
              variant="outlined"
              label={label}
              margin="dense"
              className={classes.heightReduce}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                ...params.InputProps,
                classes: {
                  notchedOutline: highlightTextField ? classes.notchedOutline : undefined,
                  input: smallInput ? classes.small : undefined,
                },
                endAdornment: (
                  <>
                    {isLoading && <CircularProgress color="secondary" size={20} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderTags={(tagValues, getTagProps) =>
            tagValues.map((option, index) => {
              const tagProps: any = getTagProps({ index });
              return (
                <Chip
                  {...tagProps}
                  size="small"
                  className={clsx(
                    classes.chip,
                    noLabel && classes.noLabel,
                    !hierarchicalOptions.includes(option) && classes.hidden,
                  )}
                  onDelete={onChipDelete ? () => onChipDelete(option) : tagProps.onDelete}
                  label={getLabel(options, option)}
                  avatar={
                    iconType ? (
                      <Avatar src={getIcon(iconType, option)} imgProps={{ style: { transform: 'scale(1.1)' } }} />
                    ) : undefined
                  }
                />
              );
            })
          }
        />
      )}
    />
  );
};

export default FilterAutocompleteGroupSelect;
